<template>
  <NotificationGroup
      group="bottom"
  >
    <div class="fixed inset-x-0 bottom-0 flex items-start justify-end p-6 px-4 py-6 pointer-events-none">
      <div class="w-full max-w-sm">
        <Notification
            v-slot="{ notifications }"
            enter="ease-out duration-300 transition"
            enter-from="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-4"
            enter-to="translate-y-0 opacity-100 sm:translate-x-0"
            leave="transition ease-in duration-500"
            leave-from="opacity-100"
            leave-to="opacity-0"
            move="transition duration-500"
            move-delay="delay-300"
        >
          <div
              v-for="notification in notifications"
              :key="notification.id"
              class="flex w-full max-w-sm mx-auto mt-4 overflow-hidden bg-white rounded-lg shadow-md"
          >
            <transition enter-active-class="transform ease-out duration-300 transition"
                        enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
                        enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
                        leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100"
                        leave-to-class="opacity-0">
              <div
                  class="max-w-sm w-full bg-white shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden">
                <div class="p-4">
                  <div class="flex items-start">
                    <div class="flex-shrink-0">
                      <CheckCircleIcon v-if="notification.type === 'success'" class="h-10 w-10 text-green-400"
                                       aria-hidden="true"/>
                      <ExclamationCircleIcon v-if="notification.type === 'error'" class="h-10 w-10 text-warning"
                                             aria-hidden="true"/>
                    </div>
                    <div class="ml-3 w-0 flex-1 pt-0.5">
                      <p class="text-base font-medium text-gray-900">{{ notification.title }}</p>
                      <p class="mt-1 text-sm text-gray-500">{{ notification.text }}</p>
                    </div>
                    <div class="ml-4 flex-shrink-0 flex">
                      <button type="button" @click="show = false"
                              class="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                        <span class="sr-only">Close</span>
                        <XIcon class="h-5 w-5" aria-hidden="true"/>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </Notification>
      </div>
    </div>
  </NotificationGroup>

  <!-- Transaction Overview -->
  <div class="bg-white shadow sm:rounded-lg mb-4" v-if="transactionData.company">
    <div class="px-4 py-4 sm:px-6">
      <div class="flex items-start justify-between">
        <div class="flex items-start space-x-4">
          <img v-if="transactionData.company.avatar" class="h-14 w-14 rounded-full" :src="transactionData.company.avatar" alt=""/>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-3">
              <h2 class="text-xl font-bold text-gray-900">{{ transactionData.company.name }}</h2>
              <router-link :to="`/company/${transactionData.company.id}`">
                <InformationCircleIcon class="h-5 w-5 text-gray-400 hover:text-gray-600" aria-hidden="true"/>
              </router-link>
            </div>
            <div class="mt-1 flex items-center gap-4 text-sm text-gray-500">
              <span>{{ transactionData.from_name }}</span>
              <span>•</span>
              <span>{{ formatDate(transactionData.date) }}</span>
              <span>•</span>
              <span class="font-semibold" :class="parseFloat(transactionData.amount) < 0 ? 'text-red-600' : 'text-green-600'">
                {{ transactionData.amount }} {{ transactionData.local_currency }}
              </span>
              <span v-if="transactionData.bank">•</span>
              <span v-if="transactionData.bank" class="inline-flex items-center">
                <CreditCardIcon class="h-4 w-4 mr-1 text-gray-400" />
                {{ transactionData.bank.name }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Transaction Details & Status -->
  <div class="bg-white shadow sm:rounded-lg mb-4">
    <div class="px-4 py-4 sm:px-6">
      <h3 class="text-base font-semibold text-gray-900 mb-3">Informations et statut</h3>
      <form>
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <RadioGroup v-model="transactionData.has_invoice">
              <RadioGroupLabel class="text-sm font-medium text-gray-700 mb-2">Statut</RadioGroupLabel>
              <div class="grid grid-cols-1 gap-2">
                <RadioGroupOption as="template" v-for="status in statusList" :key="status.id"
                                  :value="status.id" v-slot="{ checked }">
                  <div
                      :class="[checked ? 'border-indigo-500 ring-1 ring-indigo-500 bg-indigo-50' : 'border-gray-300', 'relative border rounded-md px-3 py-2 flex cursor-pointer focus:outline-none']">
                    <span class="flex-1 flex items-center">
                      <RadioGroupLabel as="span" class="text-sm font-medium text-gray-900">
                        {{ status.title }}
                      </RadioGroupLabel>
                    </span>
                    <CheckCircleIcon :class="[!checked ? 'invisible' : '', 'h-5 w-5 text-indigo-600']"
                                     aria-hidden="true"/>
                  </div>
                </RadioGroupOption>
              </div>
            </RadioGroup>
          </div>

          <div class="space-y-3">
            <div>
              <label class="block text-sm font-medium text-gray-700">Description</label>
              <input type="text" v-model="transactionData.description"
                     class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"/>
            </div>
            <div class="flex justify-end">
              <button type="button" @click="postNow"
                      class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>

  <!-- Invoices Section -->
  <div class="bg-white shadow sm:rounded-lg mb-4" v-if="transactionData.invoice && transactionData.invoice.length > 0">
    <div class="px-4 py-4 sm:px-6">
      <div class="flex items-center justify-between mb-3">
        <h3 class="text-base font-semibold text-gray-900">Factures associées</h3>
        <span class="text-sm text-gray-500">Total: {{ invoice_total_amount }} €</span>
      </div>

      <!-- Associated Invoices List -->
      <div class="space-y-2">
        <div v-for="invoice in transactionData.invoice" :key="invoice.id"
             class="relative rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 hover:border-gray-300 hover:bg-gray-100 transition-colors">
          <router-link :to="`/invoice/${invoice.id}`" class="block">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-3 flex-1 min-w-0">
                <img v-if="invoice.company && invoice.company.avatar"
                     :src="invoice.company.avatar"
                     class="h-10 w-10 rounded-full flex-shrink-0"
                     alt=""/>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-gray-900 truncate">
                    {{ invoice.company ? invoice.company.name : 'Non défini' }}
                  </p>
                  <p class="text-xs text-gray-500">
                    {{ invoice.date }}
                  </p>
                </div>
              </div>
              <div class="flex flex-col items-end ml-4">
                <span class="text-sm font-semibold text-gray-900">{{ invoice.amount }} €</span>
                <span class="text-xs text-gray-500">{{ invoice.amount_tax }} € TTC</span>
              </div>
            </div>
          </router-link>
        </div>
      </div>
    </div>
  </div>

  <IssuesLinkPanel v-if="transactionData && transactionData.id" :type="'transaction'" :id="transactionData.id"
                   :default-title="transactionData.company ? transactionData.company.name : (transactionData.from_name || '')" class="mb-4" />
</template>


<script setup>
import IssuesLinkPanel from '@/components/board/IssuesLinkPanel.vue';
import {ref} from 'vue'
import {ExclamationCircleIcon, CheckCircleIcon, InformationCircleIcon, CreditCardIcon, XIcon} from '@heroicons/vue/solid'
import {RadioGroup, RadioGroupLabel, RadioGroupOption} from '@headlessui/vue'
import {Notification, NotificationGroup} from 'notiwind'

const statusList = [
  {id: 1, title: 'Avec justificatif'},
  {id: 0, title: 'Sans justificatif'},
  {id: 3, title: 'Remboursée'},
  {id: 4, title: 'Sans facture - Compte Associé LA'},
  {id: 5, title: 'Sans facture - Compte Associé BR'},
]

const selectedstatusList = ref(statusList[0])
</script>

<script>
import moment from 'moment'
import axios from "axios";
import {usePost} from "@/composables/fetch";

export default {
  props: ['id'],
  data: () => ({
    endpoint: 'transaction/',
    transactionData: {
      invoice: [],
      bank: null,
      company: null
    },
    isLoaded: false,
    polling: null
  }),
  computed: {
    invoice_total_amount() {
      if (!this.transactionData.invoice || !Array.isArray(this.transactionData.invoice)) {
        return 0;
      }
      return this.transactionData.invoice.reduce((sum, invoice) => {
        return sum + parseFloat(invoice.amount || 0);
      }, 0).toFixed(2);
    }
  },
  mounted() {
    this.fetchTransactionDataTransaction()
    this.intervalFetchData();
  },
  beforeUnmount() {
    clearInterval(this.polling)
  },
  methods: {
    intervalFetchData: function () {
      this.polling = setInterval(() => {
        this.fetchTransactionDataTransaction();
      }, 10000);
    },
    formatDate(dateString) {
      return moment(dateString).format('DD/MM/YYYY');
    },
    async postNow() {
      const token = await this.$auth.getAccessTokenSilently();
      await usePost(this.endpoint + this.id, this.transactionData, token)
      await this.fetchTransactionDataTransaction();
    },
    async fetchTransactionDataTransaction() {
      const token = await this.$auth.getAccessTokenSilently();
      const HTTP = axios.create({
        baseURL: `https://billing.clafoutis.eu/api`,
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      HTTP.get(this.endpoint + this.id)
          .then(response => {
            const {transaction} = response.data
            if (this.transactionData !== transaction) {
              this.transactionData = transaction
            }
            this.isLoaded = true;
          })
          .catch(e => {
            console.log(e)
          })
    },
  }
}

</script>
